import React, { MouseEventHandler, useEffect, useMemo, useState } from 'react';

import type { DrawerProps } from 'antd/es/drawer';
import { useQuery } from '@tanstack/react-query';

import { SearchViewModel, WebUrlType } from '@api/index';
import {
  CloseLine,
  MapPin2Line,
  Search2Line,
  DeleteBin5Line,
} from '@assets/icons';
import { getLocationIcon } from '@components/ui/search/SearchLocation/utils/getLocationIcon';
import CustomIcon from '@components/utils/CustomIcon';
import {
  BookerSearchViewModel,
  PastSearchesHeader,
} from '@components/views/landing/components/booker/common/BookerSearchDrawer';
import { ClearSearchButton } from '@components/views/landing/components/booker/common/styled';
import replaceTrToEn from '@utils/replaceTrToEn';
import makeBold from '@utils/makeBold';
import { callService } from '@api/caller';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';

import {
  ListingSearchInput,
  ListItemsWrappedByHeader,
  ResponsiveListingSearchLocationSelect,
  ResponsiveLocationDrawerContent,
  ResponsiveLocationItems,
  ResponsiveLocationListItem,
  ResponsiveLocationListItemCityText,
  ResponsiveLocationListItemContent,
  ResponsiveLocationListItemIcon,
  ResponsiveLocationListItems,
  ResponsiveLocationListItemText,
  ResponsiveLocationListTextWrapper,
  ResponsiveSearchDrawerButton,
  ResponsiveSearchDrawerButtonWrapper,
  StyledListingLocationDrawer,
} from './ResponsiveListingSearchStyle';
import ResponsiveSearchDrawerItem from './ResponsiveSearchDrawerItem';
import { ItemErrorText } from './styled';

type CustomHeaderType =
  | React.Component<{}, {}, any>
  | React.ReactElement<any, any>;

interface LocationDrawerInterface {
  showLocationModal: () => void;
  locationInput: string;
  inputRef?: any;
  autoCompleteData?: SearchViewModel[];
  locationInputError: boolean;
  handleCloseLocationSearch: () => void;
  handleUpdateButton: (location: BookerSearchViewModel) => void;
  openAutoCompleteResultModal: boolean;
  setOpenAutoCompleteResultModal: React.Dispatch<React.SetStateAction<boolean>>;
  pastSearchesData?: BookerSearchViewModel[];
  headerComponent?: CustomHeaderType;
  onClearClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  callBack?: (item: BookerSearchViewModel) => void;
  onRemoveItem?: (item: BookerSearchViewModel) => void;
  error?: boolean;
  type?: string;
}

const ResponsiveListingSearchLocation: React.FC<
  DrawerProps & LocationDrawerInterface
> = (props) => {
  const {
    showLocationModal,
    locationInput,
    inputRef,
    handleUpdateButton,
    handleCloseLocationSearch,
    headerComponent,
    setOpenAutoCompleteResultModal,
    pastSearchesData,
    onClearClick,
    callBack,
    error,
    onRemoveItem,
    open,
    ...otherProps
  } = props;
  const [selectedLocationInput, setSelectedLocationInput] =
    useState<string>(locationInput);
  const [selectedLocation, setSelectedLocation] = useState<SearchViewModel>();
  const [filterResult, setFilterResult] = useState<SearchViewModel[]>([]);
  const [isLocationError, setIsLocationError] = useState(false);
  // 500ms debounce state for location input
  const [debouncedLocationInput, setDebouncedLocationInput] =
    useState<string>(locationInput);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLocationInput(selectedLocationInput);
    }, 500);
    return () => clearTimeout(handler);
  }, [selectedLocationInput]);

  // ================================================
  // Fetch locations/hotels and fill the search by location
  // ================================================
  const fetchLocationsItems = async () => {
    return callService('SearchesService', 'searchesHotel', {
      s: debouncedLocationInput.split('-').join(' ').toLocaleLowerCase(),
    });
  };

  const { isLoading, isError, fetchStatus } = useQuery(
    ['locationItems', debouncedLocationInput],
    fetchLocationsItems,
    {
      enabled: Boolean(
        debouncedLocationInput.length >= 3 &&
          selectedLocation?.url !== debouncedLocationInput
      ),
      onSuccess: (data) => {
        if (data && Array.isArray(data) && data.length > 0) {
          setOpenAutoCompleteResultModal(true);
          setFilterResult(
            data.filter((i: SearchViewModel) =>
              replaceTrToEn(i.text)
                .toLowerCase()
                .includes(replaceTrToEn(selectedLocationInput).toLowerCase())
            )
          );
          if (!selectedLocation && debouncedLocationInput === locationInput) {
            const selectedLocationData = data.find(
              (d) =>
                d.url?.toLocaleLowerCase() ===
                selectedLocationInput.toLocaleLowerCase()
            );

            if (selectedLocationData?.text) {
              setSelectedLocation(selectedLocationData);
              setSelectedLocationInput(selectedLocationData?.text || '');
              isLocationError && setIsLocationError(false);
            }
          }
        }
      },
    }
  );

  useEffect(() => {
    if (selectedLocationInput === '') {
      setSelectedLocation(undefined);
    }
  }, [selectedLocationInput]);

  useEffect(() => {
    if (!open) {
      setSelectedLocationInput(locationInput);
      setSelectedLocation(undefined);
      setIsLocationError(false);
    }
  }, [open]);

  const autoCompleteListItemClick = (i: any) => {
    setOpenAutoCompleteResultModal(false);
    setSelectedLocation(i);
    setSelectedLocationInput(i?.text || '');
    isLocationError && setIsLocationError(false);
  };

  const handleSelectedLocationInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setSelectedLocationInput(value);

    if (selectedLocation?.text !== value) {
      setSelectedLocation(undefined);
    }

    if (value && isLocationError) {
      setIsLocationError(false);
    }
  };

  const handleApplyButton = () => {
    if (!selectedLocation) {
      setIsLocationError(true);
      return;
    }
    handleUpdateButton(selectedLocation);
  };

  const isFilled = useMemo(
    () => selectedLocationInput.length > 0,
    [selectedLocationInput]
  );

  const getListItemsWrappedByHeader = (
    data: any,
    header: CustomHeaderType,
    historyData?: boolean
  ) => (
    <ListItemsWrappedByHeader>
      <>
        {header}
        {getListItems(data, historyData)}
      </>
    </ListItemsWrappedByHeader>
  );

  const getListItems = (data: any, historyData?: boolean) => {
    const handleItemClick = (item: SearchViewModel) => {
      autoCompleteListItemClick(
        historyData
          ? {
              id: item?.id,
              text: item?.text,
              type: item?.type as WebUrlType,
              url: item?.url,
            }
          : item
      );
      historyData && callBack?.(item);
    };

    return isLoading && fetchStatus === 'fetching' ? null : isError ? (
      <ResponsiveLocationListItem>
        <ItemErrorText>
          Aradığınız kriterlere uygun sonuç bulunamadı.
        </ItemErrorText>
      </ResponsiveLocationListItem>
    ) : data && Array.isArray(data) && data.length ? (
      <ResponsiveLocationListItems>
        {data?.map((i: SearchViewModel) => (
          <ResponsiveLocationListItem key={i.id}>
            <ResponsiveLocationListItemContent
              onClick={() => handleItemClick(i)}
            >
              <ResponsiveLocationListItemIcon>
                {i.type ? (
                  <CustomIcon
                    paletteColor='grey600'
                    icon={getLocationIcon(i.type)}
                    fontSize={20}
                  />
                ) : null}
              </ResponsiveLocationListItemIcon>
              <ResponsiveLocationListTextWrapper>
                <ResponsiveLocationListItemText
                  dangerouslySetInnerHTML={{
                    __html: makeBold(i.text || '', selectedLocationInput),
                  }}
                />
                {i?.city || i?.country ? (
                  <ResponsiveLocationListItemCityText>
                    {i?.city
                      ? i?.country
                        ? `${i?.city}, ${i?.country}`
                        : `${i?.city}`
                      : i?.country
                      ? `${i?.country}`
                      : ''}
                  </ResponsiveLocationListItemCityText>
                ) : null}
              </ResponsiveLocationListTextWrapper>
            </ResponsiveLocationListItemContent>

            {historyData ? (
              <ClearSearchButton
                onClick={onRemoveItem ? () => onRemoveItem(i) : undefined}
              >
                <CustomIcon
                  paletteColor='grey600'
                  icon={DeleteBin5Line}
                  fontSize={16}
                />
              </ClearSearchButton>
            ) : null}
          </ResponsiveLocationListItem>
        ))}
      </ResponsiveLocationListItems>
    ) : null;
  };

  const handleCloseModal = () => {
    handleCloseLocationSearch();
  };

  const showPastSearch = Array.isArray(pastSearchesData)
    ? pastSearchesData?.length > 0 && open && selectedLocationInput.length === 0
    : false;

  const showFilteredItems = selectedLocationInput.length > 2;
  return (
    <>
      <ResponsiveSearchDrawerItem
        title='Nereye?'
        subTitle={selectedLocation?.text || selectedLocationInput || ''}
        icon={MapPin2Line}
        arrowIcon={false}
        onClick={showLocationModal}
        error={error}
        isFilled={isFilled}
      />

      <StyledListingLocationDrawer
        closeIcon={
          <CustomIcon
            icon={CloseLine}
            fontSize={24}
            paletteColor='whiteAlpha600'
          />
        }
        onClose={handleCloseModal}
        open={open}
        {...otherProps}
      >
        <ResponsiveLocationDrawerContent>
          <ResponsiveListingSearchLocationSelect>
            <LocationAnimationErrorBox hasError={isLocationError || false}>
              <ListingSearchInput
                prefix={
                  <CustomIcon
                    icon={Search2Line}
                    paletteColor='grey500'
                    fontSize={20}
                  />
                }
                placeholder='Otel adı veya konum'
                value={selectedLocationInput}
                onChange={handleSelectedLocationInputChange}
                ref={inputRef}
                allowClear
              />
            </LocationAnimationErrorBox>
          </ResponsiveListingSearchLocationSelect>

          <ResponsiveLocationItems>
            {showPastSearch
              ? getListItemsWrappedByHeader(
                  pastSearchesData,
                  <PastSearchesHeader onClearClick={onClearClick} />,
                  true
                )
              : null}

            {showFilteredItems
              ? headerComponent
                ? getListItemsWrappedByHeader(filterResult, headerComponent)
                : getListItems(filterResult)
              : null}
          </ResponsiveLocationItems>
          <ResponsiveSearchDrawerButtonWrapper>
            <ResponsiveSearchDrawerButton onClick={handleApplyButton}>
              Uygula
            </ResponsiveSearchDrawerButton>
          </ResponsiveSearchDrawerButtonWrapper>
        </ResponsiveLocationDrawerContent>
      </StyledListingLocationDrawer>
    </>
  );
};

export default ResponsiveListingSearchLocation;
